import type {
  AlertChannel,
  AlertFlag,
  AlertMatrixContent,
  AlertSeverity,
  LibraryAssetContent,
} from "@/api/library";
import type { DestinationRefusalCode } from "@/lib/alertDestinations";
import {
  DESTINATION_REFUSAL_MESSAGES,
  classifyDestinationRef,
} from "@/lib/alertDestinations";

/**
 * The editable projection of an alert matrix, and the rules the browser can
 * enforce about it without duplicating the server.
 *
 * WHY A PROJECTION AT ALL. The stored shape is a set: `domains[].rules[]` holds
 * at most one rule per flag, and `routing[]` holds at most one entry per
 * severity. Neither is a thing a clinician can type INTO — removing the last
 * rule of a domain mid-edit produces content the contract refuses, and a
 * half-typed `minCount` is not an integer. The draft therefore carries every
 * severity row and both flag slots ALWAYS, with emptiness expressed as "no
 * severity chosen" and "no channel selected", and numbers as text buffers.
 * Conversion back to the wire shape happens only on Save.
 *
 * WHAT IS CHECKED HERE, AND WHY THAT IS NOT A SECOND OPINION.
 *
 * Everything below is stated on the WIRE MODEL in `schemas/library.py` —
 * `AlertMatrixContent._coherent`, `SeverityRouting._templates_match_channels`,
 * `AlertDomain._one_rule_per_flag`, and `_reject_unknown_placeholders`. Those
 * are knowable to the browser and, unlike the parts manifest that
 * `lib/assetContent.ts` deliberately refuses to mirror, each one is attributable
 * to an exact control on screen. Catching them before the request is a better
 * message, not a competing authority: the server validates again, and its
 * field-pointed 422 is what decides.
 *
 * DESTINATIONS ARE REFERENCES, NEVER VALUES. `destinationRefs` is slug-shaped by
 * contract precisely so an address, a phone number or a webhook URL cannot fit
 * (`schemas/library.py`, `AlertMatrixContent.destination_refs`). The editor
 * refuses anything else locally too, and says why — this is the prototype defect
 * #35 exists to correct, and a refusal that arrives only from the server would
 * have already put the value in a request body.
 *
 * The slug shape alone is not enough, which is what #637 adds: `oncall_example_com`,
 * `tel_15551234567` and an issuer-prefixed token are all legal slugs and none of
 * them is a name. `lib/alertDestinations.ts` mirrors the server's structural
 * refusal for exactly the rows below. The server then checks the submitted ref
 * against its current deployment-mounted authority before mutation; the browser
 * fetches that same value-free registry to offer active choices, but never
 * promotes its cached copy into authority over the server write.
 */

export const ALERT_SEVERITIES = ["high", "medium", "low"] as const satisfies
  readonly AlertSeverity[];
export const ALERT_FLAGS = ["red", "yellow"] as const satisfies readonly AlertFlag[];
export const ALERT_CHANNELS = ["email", "sms", "webhook"] as const satisfies
  readonly AlertChannel[];

export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const FLAG_LABELS: Record<AlertFlag, string> = {
  red: "Red flags",
  yellow: "Yellow flags",
};

export const CHANNEL_LABELS: Record<AlertChannel, string> = {
  email: "Email",
  sms: "SMS",
  webhook: "Webhook",
};

/**
 * The closed placeholder set, mirroring `ALERT_PLACEHOLDERS` in
 * `schemas/library.py`. Unknown tokens are rejected rather than rendered as
 * literal braces at alert time.
 */
export const ALERT_PLACEHOLDERS = [
  "domain",
  "patient_name",
  "rule",
  "severity",
] as const;

/** `SlugKey` and the `destinationRefs` item pattern, both from the contract. */
export const ALERT_SLUG_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** Contract bounds. Named so a message can quote the number it enforces. */
export const MAX_DOMAINS = 24;
export const MAX_COMBOS = 24;
export const MAX_DESTINATIONS = 24;
/** `BoundedName`. */
export const MAX_NAME_CHARS = 200;
/** `ShortText`, the bound on every message template. */
export const MAX_TEMPLATE_CHARS = 2000;

export interface DraftDomainRule {
  /**
   * `""` means this domain defines no rule for this flag.
   *
   * A domain carries at most one rule per flag by contract, so the flag itself
   * is the slot and the severity is what fills it.
   */
  severity: AlertSeverity | "";
  /** Text, not a number: `1` backspaced to `""` is a state, not a zero. */
  minCount: string;
  /**
   * `DomainRule.label`, edited exactly like a combo's.
   *
   * `""` is the absence of a label, and serialises to `null` — the contract
   * makes it optional, and an empty string is not a label. Carrying it was the
   * minimum (publishing must not delete stored labels); making it EDITABLE is
   * what keeps it honest, because a label describes a threshold the clinician
   * can change underneath it. A preserved-but-invisible label survives an edit
   * to the count it describes and silently contradicts it.
   */
  label: string;
}

export interface DraftDomain {
  /** React identity, distinct from `key`, which the clinician edits. */
  id: string;
  key: string;
  displayName: string;
  rules: Record<AlertFlag, DraftDomainRule>;
}

export interface DraftCombo {
  id: string;
  flag: AlertFlag;
  minDomains: string;
  severity: AlertSeverity;
  label: string;
}

export interface DraftDestination {
  id: string;
  ref: string;
}

export interface DraftRouting {
  severity: AlertSeverity;
  channels: AlertChannel[];
  emailSubject: string;
  emailBody: string;
  smsBody: string;
}

export interface DraftAlertMatrix {
  domains: DraftDomain[];
  combos: DraftCombo[];
  destinations: DraftDestination[];
  /** Always one row per severity, in `ALERT_SEVERITIES` order. */
  routing: DraftRouting[];
}

/**
 * One refusal, attributable to a control.
 *
 * `path` is the field-anchor path of the control it belongs to, or
 * `"content"` when the problem belongs to the matrix as a whole — an
 * unroutable severity is a property of the set, not of any one threshold.
 */
export interface MatrixProblem {
  path: string;
  message: string;
}

export type MatrixDraftMapping =
  | { ok: true; draft: DraftAlertMatrix }
  | { ok: false; problem: MatrixProblem };

let nextDraftId = 0;

function newDraftId(prefix: string): string {
  nextDraftId += 1;
  return `draft-${prefix}-${nextDraftId}`;
}

export function isAlertMatrixContent(
  content: LibraryAssetContent,
): content is AlertMatrixContent {
  return content.kind === "alert_matrix";
}

export function domainPath(index: number): string {
  return `content.domains[${index}]`;
}

export function rulePath(index: number, flag: AlertFlag): string {
  return `content.domains[${index}].rules.${flag}`;
}

export function comboPath(index: number): string {
  return `content.combos[${index}]`;
}

export function destinationPath(index: number): string {
  return `content.destinationRefs[${index}]`;
}

export function routingPath(severity: AlertSeverity): string {
  return `content.routing.${severity}`;
}

export function templatePath(
  severity: AlertSeverity,
  field: "emailSubject" | "emailBody" | "smsBody",
): string {
  return `content.routing.${severity}.${field}`;
}

function emptyRule(): DraftDomainRule {
  return { severity: "", minCount: "1", label: "" };
}

export function newDraftDomain(): DraftDomain {
  return {
    id: newDraftId("domain"),
    key: "",
    displayName: "",
    rules: { red: emptyRule(), yellow: emptyRule() },
  };
}

export function newDraftCombo(): DraftCombo {
  return {
    id: newDraftId("combo"),
    flag: "yellow",
    // The contract's floor. A combo is a CROSS-domain escalation, so one
    // domain is not a combination of anything.
    minDomains: "2",
    severity: "medium",
    label: "",
  };
}

export function newDraftDestination(): DraftDestination {
  return { id: newDraftId("destination"), ref: "" };
}

function emptyRouting(severity: AlertSeverity): DraftRouting {
  return {
    severity,
    channels: [],
    emailSubject: "",
    emailBody: "",
    smsBody: "",
  };
}

/**
 * A matrix nobody has authored yet, at the contract's FLOOR (#494).
 *
 * `AlertMatrixContent` requires at least one domain, at least one
 * `destinationRefs` entry and at least one routing row, so a genuinely empty
 * draft is not content a create could ever send. Starting at the floor makes
 * authoring one a matter of filling the surface in, rather than discovering a
 * refusal on Create — the same reason `emptyQuestionnaireDraft` starts at one
 * item and two response points.
 *
 * Assembled from the part constructors above rather than describing a second
 * draft model beside them: a domain, a destination and the severity rows are
 * already defined once each, and `toMatrixDraft` builds the routing the same
 * way for exactly the same reason — every severity carries a row whether it is
 * routed or not, because the ABSENCE of routing is what the clinician edits.
 *
 * No combos: a cross-domain rule spans at least two domains, so one cannot
 * exist yet, and `findMatrixProblems` would refuse the draft it is starting
 * from.
 */
export function emptyMatrixDraft(): DraftAlertMatrix {
  return {
    domains: [newDraftDomain()],
    combos: [],
    destinations: [newDraftDestination()],
    routing: ALERT_SEVERITIES.map((severity) => emptyRouting(severity)),
  };
}

/**
 * The stored-subject invariant shared by editable and read-only matrix views.
 *
 * `emailSubject` is optional in the generated type because non-email routing
 * legitimately omits it. Once email is routed, though, an absent/null/empty
 * runtime value is unreadable content, not an authored blank.
 */
export function unreadableMatrixSubjectProblem(
  content: AlertMatrixContent,
): MatrixProblem | null {
  for (const route of content.routing) {
    if (
      route.channels.includes("email") &&
      (typeof route.emailSubject !== "string" || route.emailSubject.length === 0)
    ) {
      return {
        path: templatePath(route.severity, "emailSubject"),
        message: `${SEVERITY_LABELS[route.severity]} email routing has no readable subject. The stored value may be unavailable, redacted, or malformed; it was not replaced with a blank draft.`,
      };
    }
  }
  return null;
}

/**
 * The stored content, opened for editing, or the exact field that made doing
 * so unsafe.
 *
 * Generated types describe the service's canonical wire shape, but they do
 * not validate JSON at runtime. In particular, `emailSubject` is optional in
 * OpenAPI because a non-email route legitimately omits it. That must never let
 * an EMAIL route with an omitted, null, or malformed subject fall through as
 * an editable empty string: an unrelated publish would then overwrite stored
 * clinical wording with the fallback. Fail before creating a draft instead.
 */
export function toMatrixDraft(content: AlertMatrixContent): MatrixDraftMapping {
  const subjectProblem = unreadableMatrixSubjectProblem(content);
  if (subjectProblem) {
    return {
      ok: false,
      problem: subjectProblem,
    };
  }

  return {
    ok: true,
    draft: {
      domains: content.domains.map((domain) => {
        const rules: Record<AlertFlag, DraftDomainRule> = {
          red: emptyRule(),
          yellow: emptyRule(),
        };
        for (const rule of domain.rules) {
          rules[rule.flag] = {
            severity: rule.severity,
            minCount: String(rule.minCount),
            label: rule.label ?? "",
          };
        }
        return {
          id: newDraftId("domain"),
          key: domain.key,
          displayName: domain.displayName,
          rules,
        };
      }),
      combos: (content.combos ?? []).map((combo) => ({
        id: newDraftId("combo"),
        flag: combo.flag,
        minDomains: String(combo.minDomains),
        severity: combo.severity,
        label: combo.label ?? "",
      })),
      destinations: content.destinationRefs.map((ref) => ({
        id: newDraftId("destination"),
        ref,
      })),
      // Every severity gets a row whether the stored matrix routes it or not:
      // the absence of a routing entry is what the clinician edits, so it needs
      // a control rather than an "add routing" button that re-creates the row
      // they just emptied.
      routing: ALERT_SEVERITIES.map((severity) => {
        const stored = content.routing.find(
          (entry) => entry.severity === severity,
        );
        if (!stored) {
          return emptyRouting(severity);
        }
        return {
          severity,
          channels: ALERT_CHANNELS.filter((channel) =>
            stored.channels.includes(channel),
          ),
          // The preflight above proved this is a string whenever email is live.
          // A non-email row has no subject control, so its legitimate absence is
          // represented only in that inactive draft buffer.
          emailSubject:
            typeof stored.emailSubject === "string" ? stored.emailSubject : "",
          emailBody: stored.emailBody ?? "",
          smsBody: stored.smsBody ?? "",
        };
      }),
    },
  };
}

/**
 * The whole brace STRUCTURE, not just the token names — the exact rule at
 * `schemas/library.py::_reject_unknown_placeholders`, in the server's own
 * sentences.
 *
 * An unknown token is a typo, but so is `{patient_name` or `{{patient_name}}`:
 * common renderers leave those literal, so a clinician's alert arrives with raw
 * braces in it. Nesting and unbalanced braces are refused outright rather than
 * scanned past.
 */
export function placeholderProblem(template: string): string | null {
  const allowed = [...ALERT_PLACEHOLDERS].join(", ");
  let depth = 0;
  let token = "";
  for (const char of template) {
    if (char === "{") {
      if (depth) {
        return "nested '{' in a message template; placeholders cannot be nested or escaped";
      }
      depth = 1;
      token = "";
    } else if (char === "}") {
      if (!depth) {
        return "unmatched '}' in a message template";
      }
      depth = 0;
      if (!(ALERT_PLACEHOLDERS as readonly string[]).includes(token)) {
        return `unknown placeholder {${token}}; allowed: ${allowed}`;
      }
    } else if (depth) {
      token += char;
    }
  }
  return depth ? "unclosed '{' in a message template" : null;
}

/**
 * The patient-name exposure warning — NON-BLOCKING, and frontend-only.
 *
 * `ALERT_PLACEHOLDERS` permits `patient_name` anywhere, and the seeded default
 * happens to avoid it; authored content has nothing stopping it. An email
 * SUBJECT and an SMS BODY are the two places an alert's text is read without
 * opening anything — a locked phone screen, a notification preview, a mail
 * server's subject log — so a name there travels further than the alert does.
 *
 * It is a warning rather than a refusal because the server accepts it and no
 * ticket authorizes tightening the schema: refusing here would make the editor
 * stricter than the contract, and a matrix authored elsewhere would then be
 * unopenable. Narrowing `ALERT_PLACEHOLDERS` server-side is a contract change
 * capable of invalidating stored content and belongs in its own pull request.
 */
export function patientNameWarning(
  field: "emailSubject" | "smsBody",
  template: string,
): string | null {
  if (!template.includes("{patient_name}")) {
    return null;
  }
  const where =
    field === "emailSubject"
      ? "an email subject line"
      : "an SMS body";
  const seen =
    field === "emailSubject"
      ? "in mail-server logs and on a locked phone screen"
      : "on a locked phone screen and by the SMS carrier";
  return `{patient_name} puts the patient's name in ${where}, where it is read ${seen}. The Editor Service accepts it; {domain} and {severity} identify the alert without naming the patient.`;
}

/** Every non-blocking warning the draft currently raises, with its control. */
export function findMatrixWarnings(
  draft: DraftAlertMatrix,
): MatrixProblem[] {
  const warnings: MatrixProblem[] = [];
  for (const route of draft.routing) {
    if (route.channels.includes("email")) {
      const message = patientNameWarning("emailSubject", route.emailSubject);
      if (message) {
        warnings.push({
          path: templatePath(route.severity, "emailSubject"),
          message,
        });
      }
    }
    if (route.channels.includes("sms")) {
      const message = patientNameWarning("smsBody", route.smsBody);
      if (message) {
        warnings.push({ path: templatePath(route.severity, "smsBody"), message });
      }
    }
  }
  return warnings;
}

function parseCount(value: string): number | null {
  return /^\d+$/.test(value.trim()) ? Number.parseInt(value.trim(), 10) : null;
}

function checkTemplate(
  problems: MatrixProblem[],
  path: string,
  label: string,
  value: string,
): void {
  if (value.length > MAX_TEMPLATE_CHARS) {
    problems.push({
      path,
      message: `${label} is at most ${MAX_TEMPLATE_CHARS.toLocaleString("en-US")} characters; this one is ${value.length.toLocaleString("en-US")}.`,
    });
    return;
  }
  const placeholder = placeholderProblem(value);
  if (placeholder) {
    problems.push({ path, message: `${label}: ${placeholder}.` });
  }
}

/**
 * Every blocking problem in the draft, each attributed to the control it is
 * about.
 *
 * Ordered the way a clinician reads the surface — domains, then combinations,
 * then destinations, then routing, then the whole-matrix coherence checks that
 * only make sense once the parts are individually valid.
 */
export function findMatrixProblems(draft: DraftAlertMatrix): MatrixProblem[] {
  const problems: MatrixProblem[] = [];

  if (draft.domains.length === 0) {
    problems.push({
      path: "content.domains",
      message: "An alert matrix needs at least one domain.",
    });
  }
  if (draft.domains.length > MAX_DOMAINS) {
    problems.push({
      path: "content.domains",
      message: `An alert matrix carries at most ${MAX_DOMAINS} domains.`,
    });
  }

  const seenKeys = new Map<string, number>();
  draft.domains.forEach((domain, index) => {
    const path = domainPath(index);
    if (domain.key.length === 0) {
      problems.push({ path, message: "This domain needs a key." });
    } else if (!ALERT_SLUG_PATTERN.test(domain.key)) {
      problems.push({
        path,
        message:
          'A domain key is a lowercase slug — "mood", "sleep_quality" — of at most 64 characters.',
      });
    } else {
      const first = seenKeys.get(domain.key);
      if (first === undefined) {
        seenKeys.set(domain.key, index);
      } else {
        problems.push({
          path,
          message: `Domain keys must be unique; this key is already used by domain ${first + 1}.`,
        });
      }
    }

    if (domain.displayName.trim().length === 0) {
      problems.push({ path, message: "This domain needs a display name." });
    } else if (domain.displayName.length > MAX_NAME_CHARS) {
      problems.push({
        path,
        message: `A display name is at most ${MAX_NAME_CHARS} characters.`,
      });
    }

    const active = ALERT_FLAGS.filter(
      (flag) => domain.rules[flag].severity !== "",
    );
    if (active.length === 0) {
      problems.push({
        path,
        message:
          "A domain needs at least one threshold; a domain that raises nothing cannot contribute to an alert.",
      });
    }
    for (const flag of active) {
      const count = parseCount(domain.rules[flag].minCount);
      if (count === null || count < 1) {
        problems.push({
          path: rulePath(index, flag),
          message: `${FLAG_LABELS[flag]}: the threshold is a whole number of at least 1.`,
        });
      }
      if (domain.rules[flag].label.length > MAX_NAME_CHARS) {
        problems.push({
          path: rulePath(index, flag),
          message: `A label is at most ${MAX_NAME_CHARS} characters.`,
        });
      }
    }
  });

  if (draft.combos.length > MAX_COMBOS) {
    problems.push({
      path: "content.combos",
      message: `An alert matrix carries at most ${MAX_COMBOS} cross-domain rules.`,
    });
  }
  draft.combos.forEach((combo, index) => {
    const path = comboPath(index);
    const count = parseCount(combo.minDomains);
    if (count === null || count < 2) {
      problems.push({
        path,
        message:
          "A cross-domain rule spans at least 2 domains; one domain is not a combination.",
      });
    } else {
      // Only domains that actually carry the combo's flag can contribute, so
      // counting every domain would accept a rule nothing can trigger.
      const eligible = draft.domains.filter(
        (domain) => domain.rules[combo.flag].severity !== "",
      ).length;
      if (count > eligible) {
        problems.push({
          path,
          message: `This rule needs ${count} domains but only ${eligible} define a ${combo.flag} threshold, so nothing can trigger it.`,
        });
      }
    }
    if (combo.label.length > MAX_NAME_CHARS) {
      problems.push({
        path,
        message: `A label is at most ${MAX_NAME_CHARS} characters.`,
      });
    }
  });

  if (draft.destinations.length === 0) {
    problems.push({
      path: "content.destinationRefs",
      message:
        "An alert matrix needs at least one destination reference, or its alerts reach nobody.",
    });
  }
  if (draft.destinations.length > MAX_DESTINATIONS) {
    problems.push({
      path: "content.destinationRefs",
      message: `An alert matrix carries at most ${MAX_DESTINATIONS} destination references.`,
    });
  }
  const seenRefs = new Map<string, number>();
  draft.destinations.forEach((destination, index) => {
    const path = destinationPath(index);
    const ref = destination.ref;
    if (ref.length === 0) {
      problems.push({ path, message: "This destination needs a reference." });
    } else if (!ALERT_SLUG_PATTERN.test(ref)) {
      problems.push({
        path,
        message:
          "A destination reference is a lowercase slug naming a configured destination — never an address, a phone number, or a URL. The addresses themselves are held outside clinical content.",
      });
    } else if (classifyDestinationRef(ref) !== null) {
      // Slug-shaped and still a value: `oncall_example_com`, `tel_15551234567`,
      // an issuer-prefixed token. The server refuses each of these before it
      // writes anything (`422 alert_destination_refused`); refusing here is what
      // keeps the value out of the request body in the first place.
      problems.push({
        path,
        message:
          DESTINATION_REFUSAL_MESSAGES[
            classifyDestinationRef(ref) as DestinationRefusalCode
          ],
      });
    } else {
      const first = seenRefs.get(ref);
      if (first === undefined) {
        seenRefs.set(ref, index);
      } else {
        problems.push({
          path,
          message: `Destination references must be unique; this one is already listed at position ${first + 1}.`,
        });
      }
    }
  });

  const routed = draft.routing.filter((route) => route.channels.length > 0);
  if (routed.length === 0) {
    problems.push({
      path: "content.routing",
      message: "At least one severity must dispatch to a channel.",
    });
  }

  for (const route of routed) {
    const path = routingPath(route.severity);
    if (route.channels.includes("email")) {
      if (route.emailSubject.trim().length === 0) {
        problems.push({
          path: templatePath(route.severity, "emailSubject"),
          message: "Email routing needs a subject.",
        });
      }
      if (route.emailBody.trim().length === 0) {
        problems.push({
          path: templatePath(route.severity, "emailBody"),
          message: "Email routing needs a body.",
        });
      }
      checkTemplate(
        problems,
        templatePath(route.severity, "emailSubject"),
        "Email subject",
        route.emailSubject,
      );
      checkTemplate(
        problems,
        templatePath(route.severity, "emailBody"),
        "Email body",
        route.emailBody,
      );
    }
    if (route.channels.includes("sms")) {
      if (route.smsBody.trim().length === 0) {
        problems.push({
          path: templatePath(route.severity, "smsBody"),
          message: "SMS routing needs a message.",
        });
      }
      checkTemplate(
        problems,
        templatePath(route.severity, "smsBody"),
        "SMS message",
        route.smsBody,
      );
    }
    if (route.channels.length > ALERT_CHANNELS.length) {
      problems.push({ path, message: "A severity dispatches to at most three channels." });
    }
  }

  // Coverage, fail-closed and in both directions — the same pair of checks
  // `AlertMatrixContent._coherent` runs. A rule firing into an unrouted
  // severity raises an alert that goes nowhere; a routing entry no rule can
  // reach is a clinician expecting alerts that never arrive, stored in
  // immutable content looking configured.
  const produced = new Set<AlertSeverity>();
  for (const domain of draft.domains) {
    for (const flag of ALERT_FLAGS) {
      const severity = domain.rules[flag].severity;
      if (severity !== "") {
        produced.add(severity);
      }
    }
  }
  for (const combo of draft.combos) {
    produced.add(combo.severity);
  }
  const routedSeverities = new Set(routed.map((route) => route.severity));

  for (const severity of ALERT_SEVERITIES) {
    if (produced.has(severity) && !routedSeverities.has(severity)) {
      problems.push({
        path: routingPath(severity),
        message: `A threshold can raise ${SEVERITY_LABELS[severity].toLowerCase()} severity, but nothing routes it. Choose at least one channel, or lower the thresholds that produce it.`,
      });
    }
    if (!produced.has(severity) && routedSeverities.has(severity)) {
      problems.push({
        path: routingPath(severity),
        message: `Nothing can raise ${SEVERITY_LABELS[severity].toLowerCase()} severity, so this routing never fires. Remove its channels, or give a threshold this severity.`,
      });
    }
  }

  return problems;
}

/**
 * The draft as wire content, or the reasons it is not sendable yet.
 *
 * Never partially converts: a caller either gets content it may send or a list
 * of problems it may attribute, so no path exists that publishes half a matrix.
 *
 * A template for a channel that is NOT routed is dropped rather than sent. The
 * contract refuses it in both directions — an authored message for a silent
 * channel is an authoring slip — and the draft keeps the buffer so a
 * mis-toggled channel does not destroy the wording while the session lives.
 */
export function toAlertMatrixContent(
  draft: DraftAlertMatrix,
): { ok: true; content: AlertMatrixContent } | { ok: false; problems: MatrixProblem[] } {
  const problems = findMatrixProblems(draft);
  if (problems.length > 0) {
    return { ok: false, problems };
  }

  return {
    ok: true,
    content: {
      kind: "alert_matrix",
      domains: draft.domains.map((domain) => ({
        key: domain.key,
        displayName: domain.displayName.trim(),
        rules: ALERT_FLAGS.flatMap((flag) => {
          const rule = domain.rules[flag];
          if (rule.severity === "") {
            return [];
          }
          return [
            {
              flag,
              minCount: parseCount(rule.minCount) as number,
              severity: rule.severity,
              label:
                rule.label.trim() === "" ? null : rule.label.trim(),
            },
          ];
        }),
      })),
      combos: draft.combos.map((combo) => ({
        flag: combo.flag,
        minDomains: parseCount(combo.minDomains) as number,
        severity: combo.severity,
        label: combo.label.trim() === "" ? null : combo.label.trim(),
      })),
      destinationRefs: draft.destinations.map((destination) => destination.ref),
      routing: draft.routing
        .filter((route) => route.channels.length > 0)
        .map((route) => ({
          severity: route.severity,
          channels: route.channels,
          emailSubject: route.channels.includes("email")
            ? route.emailSubject
            : null,
          emailBody: route.channels.includes("email") ? route.emailBody : null,
          smsBody: route.channels.includes("sms") ? route.smsBody : null,
        })),
    },
  };
}

/**
 * Dirty is decided on what the clinician sees, not on what it would serialise
 * to — the same rule `lib/assetContent.ts` applies to a reformatted JSON part.
 */
export function isSameMatrixDraft(
  left: DraftAlertMatrix,
  right: DraftAlertMatrix,
): boolean {
  const sameRule = (a: DraftDomainRule, b: DraftDomainRule) =>
    a.severity === b.severity &&
    a.minCount === b.minCount &&
    a.label === b.label;
  return (
    left.domains.length === right.domains.length &&
    left.combos.length === right.combos.length &&
    left.destinations.length === right.destinations.length &&
    left.domains.every((domain, index) => {
      const other = right.domains[index] as DraftDomain;
      return (
        domain.key === other.key &&
        domain.displayName === other.displayName &&
        ALERT_FLAGS.every((flag) =>
          sameRule(domain.rules[flag], other.rules[flag]),
        )
      );
    }) &&
    left.combos.every((combo, index) => {
      const other = right.combos[index] as DraftCombo;
      return (
        combo.flag === other.flag &&
        combo.minDomains === other.minDomains &&
        combo.severity === other.severity &&
        combo.label === other.label
      );
    }) &&
    left.destinations.every(
      (destination, index) =>
        destination.ref === (right.destinations[index] as DraftDestination).ref,
    ) &&
    left.routing.every((route, index) => {
      const other = right.routing[index] as DraftRouting;
      return (
        route.severity === other.severity &&
        route.channels.join(",") === other.channels.join(",") &&
        route.emailSubject === other.emailSubject &&
        route.emailBody === other.emailBody &&
        route.smsBody === other.smsBody
      );
    })
  );
}

/**
 * Which control a server refusal is about.
 *
 * The service points every alert-matrix failure at `content`, which is correct
 * and too coarse to attribute. Its sentences, however, are written around
 * vocabulary the SERVER defined — a domain key it is echoing back from the
 * content it just parsed, a severity name from its own enum — so this reads
 * structure rather than prose, and resolves to `null` whenever it cannot be
 * sure. A refusal attached to the wrong control is worse than one attached to
 * the matrix.
 */
export function attributeMatrixFailure(message: string): string | null {
  const severity = /\b(?:severities|severity)[^.]*?\b(low|medium|high)\b/.exec(
    message,
  );
  if (severity) {
    return routingPath(severity[1] as AlertSeverity);
  }
  if (/destination/i.test(message)) {
    return "content.destinationRefs";
  }
  if (/\bcombo\b/i.test(message)) {
    return "content.combos";
  }
  if (/\bdomain\b/i.test(message)) {
    return "content.domains";
  }
  if (/placeholder|template|'\{|\bbrace/i.test(message)) {
    return "content.routing";
  }
  return null;
}
